import { PRODUCT_NAME } from "@mailhand/core/brand";
import { tenantCreateSchema } from "@mailhand/core/schemas";
import { type SubmitEvent, useState } from "react";
import { navigate } from "../../app/router.js";
import { useApi, useSessions } from "../../app/services.js";
import { SignOutButton } from "../../app/signOut.js";
import { useTask } from "../../app/useTask.js";
import { Button, Eyebrow, Field, Input } from "../../ui/index.js";
import { AuthLayout } from "./AuthLayout.js";

/** "acmecoffee.com" becomes "Acmecoffee", a starting point the owner edits. */
export function nameFromDomain(domain: string | null): string {
  const label = domain?.trim().toLowerCase().replace(/^www\./, "").split(".")[0] ?? "";
  return label === "" ? "" : `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
}

export function StartScreen({ domain }: { domain: string | null }) {
  const api = useApi();
  const sessions = useSessions();
  const task = useTask();
  const [name, setName] = useState(() => nameFromDomain(domain));
  const [error, setError] = useState<string | undefined>(undefined);

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const parsed = tenantCreateSchema.safeParse({ name });
    if (!parsed.success) {
      setError("Type your business name (up to 80 characters).");
      return;
    }
    setError(undefined);
    const ok = await task.run(async () => { sessions.set(await api.call("tenantCreate", { body: parsed.data })); });
    if (ok) navigate("/setup", { replace: true });
  }

  return (
    <AuthLayout end={<SignOutButton />}>
      <Eyebrow>New business</Eyebrow>
      <h1 className="headline">Create your {PRODUCT_NAME}.</h1>
      <p className="auth-lede">One per business. You can invite your team after.</p>
      <form className="auth-form" onSubmit={(event) => void submit(event)} noValidate>
        <Field label="Business name" error={error}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              value={name}
              placeholder="Acme Coffee"
              autoComplete="organization"
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(event) => { setName(event.target.value); }}
              autoFocus
            />
          )}
        </Field>
        <Button type="submit" variant="primary" busy={task.busy}>
          Create
        </Button>
      </form>
    </AuthLayout>
  );
}
